import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useTheme } from '../../contexts/ThemeContext';
import html2canvas from 'html2canvas';
import Sidebar from '../../components/Sidebar';
import { Button, LoadingButton } from '../../components/ui/Button';

function FakeCallGenerator() {
  const { theme } = useTheme();
  const previewRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  // Phone settings
  const [deviceTime, setDeviceTime] = useState('10:05');
  const [signalLevel, setSignalLevel] = useState(4);
  const [networkType, setNetworkType] = useState('5G');
  const [batteryLevel, setBatteryLevel] = useState(60);
  const [isCharging, setIsCharging] = useState(false);
  const [showBatteryPercent, setShowBatteryPercent] = useState(false);
  const [showWifi, setShowWifi] = useState(false);
  const [wifiLevel, setWifiLevel] = useState(3);
  const [isAirplaneMode, setIsAirplaneMode] = useState(false);
  const [headerIcon, setHeaderIcon] = useState('');

  // Call settings
  const [callerName, setCallerName] = useState('Mirakle');
  const [callDuration, setCallDuration] = useState('00:00');
  const [isCallActive, setIsCallActive] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);

  // Labels
  const [labels, setLabels] = useState({
    speaker: 'speaker',
    facetime: 'FaceTime',
    mute: 'mute',
    add: 'add call',
    end: 'End',
    keypad: 'keypad'
  });

  // Background
  const [backgroundType, setBackgroundType] = useState('gradient');
  const [backgroundColor, setBackgroundColor] = useState('#1a1a2e');
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [gradientPreset, setGradientPreset] = useState('default-gray');

  // Audio
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Container size
  const [containerWidth, setContainerWidth] = useState(370);
  const [containerHeight, setContainerHeight] = useState(700);

  // Show info icon
  const [showInfoIcon, setShowInfoIcon] = useState(false);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Gradient presets
  const gradientPresets = {
    'dark-purple': 'linear-gradient(180deg, #2d1f3d 0%, #1a1a2e 30%, #16213e 60%, #1a1a2e 100%)',
    'default-gray': 'linear-gradient(180deg, #4a4a4a 0%, #3d3d3d 30%, #2d2d2d 70%, #1a1a1a 100%)',
    'sunset': 'linear-gradient(180deg, #ff6b6b 0%, #feca57 100%)',
    'ocean': 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
    'forest': 'linear-gradient(180deg, #134e5e 0%, #71b280 100%)',
    'night': 'linear-gradient(180deg, #0c0c0c 0%, #1a1a1a 50%, #2d2d2d 100%)',
    'pink': 'linear-gradient(180deg, #ee9ca7 0%, #ffdde1 100%)'
  };

  const parseDurationToSeconds = (duration) => {
    const parts = duration.split(':');
    if (parts.length === 2) {
      const minutes = parseInt(parts[0], 10) || 0;
      const seconds = parseInt(parts[1], 10) || 0;
      return minutes * 60 + seconds;
    }
    return 0;
  };

  const formatSecondsToDuration = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isCallActive) {
      timerRef.current = setInterval(() => {
        setCallDuration(prev => {
          const currentSeconds = parseDurationToSeconds(prev);
          const newSeconds = currentSeconds + 1;
          return formatSecondsToDuration(newSeconds);
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isCallActive]);

  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setAudioFile(url);
    }
  };

  const playAudio = () => {
    if (audioRef.current && audioFile) {
      audioRef.current.play();
      setIsPlaying(true);
      if (!isCallActive) {
        setIsCallActive(true);
      }
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
    setIsCallActive(false);
  };

  const handleBackgroundUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setBackgroundImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getBackgroundStyle = () => {
    if (backgroundType === 'image' && backgroundImage) {
      return {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    } else if (backgroundType === 'color') {
      return { background: backgroundColor };
    } else {
      return { background: gradientPresets[gradientPreset] };
    }
  };

  const exportImage = async () => {
    if (!previewRef.current) return;

    setIsExporting(true);
    try {
      const canvas = await html2canvas(previewRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: null
      });

      const link = document.createElement('a');
      link.download = `fake-call-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const renderSignalBars = () => {
    const bars = [];
    for (let i = 1; i <= 4; i++) {
      bars.push(
        <rect
          key={i}
          x={(i - 1) * 4}
          y={12 - i * 3}
          width="3"
          height={i * 3}
          fill={i <= signalLevel ? '#fff' : 'rgba(255,255,255,0.3)'}
          rx="0.5"
        />
      );
    }
    return (
      <svg width="16" height="12" viewBox="0 0 16 12">
        {bars}
      </svg>
    );
  };

  const renderBattery = () => {
    const fillWidth = (batteryLevel / 100) * 20;
    const fillColor = isCharging ? '#34c759' : (batteryLevel <= 20 ? '#ff3b30' : '#fff');

    return (
      <svg width="28" height="12" viewBox="0 0 28 12">
        <rect x="0" y="1" width="24" height="10" rx="2" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
        <rect x="2" y="3" width={fillWidth} height="6" rx="1" fill={fillColor} />
        <rect x="25" y="4" width="2" height="4" rx="0.5" fill="rgba(255,255,255,0.5)" />
        {isCharging && (
          <path d="M12 2L9 6h3l-2 4 5-4h-3l2-4z" fill="#34c759" />
        )}
      </svg>
    );
  };

  const renderWifi = () => {
    return (
      <svg width="16" height="12" viewBox="0 0 16 12" fill="#fff">
        <path opacity={wifiLevel >= 1 ? 1 : 0.3} d="M8 10a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" />
        <path opacity={wifiLevel >= 2 ? 1 : 0.3} d="M4.5 7.5a5 5 0 017 0" stroke="#fff" fill="none" strokeWidth="1.5" strokeLinecap="round" />
        <path opacity={wifiLevel >= 3 ? 1 : 0.3} d="M2 5a8 8 0 0112 0" stroke="#fff" fill="none" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  };

  return (
    <div className="flex min-h-screen" style={{ background: theme.bgPrimary }}>
      <Sidebar activePage="projects" />

      <div className="flex-1 p-6 overflow-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon
              icon="mdi:arrow-left"
              className="text-2xl cursor-pointer"
              style={{ color: theme.textSecondary }}
              onClick={() => window.history.back()}
            />
            <h1 className="text-2xl font-bold m-0" style={{ color: theme.textPrimary }}>
              Fake Call Generator
            </h1>
          </div>
          <p className="m-0" style={{ color: theme.textSecondary }}>
            Create realistic iPhone call screenshots with auto timer and audio playback
          </p>
        </div>

        <div className="flex gap-8 flex-wrap">
          {/* Settings Panel */}
          <div
            className="flex-[1_1_350px] max-w-[450px] rounded-2xl p-6"
            style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
          >
            {/* Phone Options */}
            <details open className="mb-5">
              <summary className="text-base font-semibold cursor-pointer mb-4" style={{ color: theme.textPrimary }}>
                Phone Options
              </summary>

              <div className="flex flex-col gap-4">
                {/* Device Time */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                    Status Bar Time
                  </label>
                  <input
                    type="text"
                    value={deviceTime}
                    onChange={(e) => setDeviceTime(e.target.value)}
                    placeholder="10:05"
                    className="w-full py-2 px-3 rounded-lg text-sm"
                    style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
                  />
                </div>

                {/* Header Icon */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                    Header Icon
                  </label>
                  <select
                    value={headerIcon}
                    onChange={(e) => setHeaderIcon(e.target.value)}
                    className="w-full py-2 px-3 rounded-lg text-sm"
                    style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
                  >
                    <option value="">No icon</option>
                    <option value="location">📍 Location</option>
                    <option value="moon">🌙 Night Mode</option>
                    <option value="silent">🔕 Silent</option>
                  </select>
                </div>

                {/* Signal Level */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                    Signal: {signalLevel} bars
                  </label>
                  <input type="range" min="1" max="4" value={signalLevel} onChange={(e) => setSignalLevel(Number(e.target.value))} className="w-full" />
                </div>

                {/* Network Type */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                    Network Type
                  </label>
                  <select
                    value={networkType}
                    onChange={(e) => setNetworkType(e.target.value)}
                    className="w-full py-2 px-3 rounded-lg text-sm"
                    style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
                  >
                    <option value="5G">5G</option>
                    <option value="LTE">LTE</option>
                    <option value="4G">4G</option>
                    <option value="3G">3G</option>
                  </select>
                </div>

                {/* Battery Level */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                    Battery: {batteryLevel}%
                  </label>
                  <input type="range" min="10" max="100" step="10" value={batteryLevel} onChange={(e) => setBatteryLevel(Number(e.target.value))} className="w-full" />
                </div>

                {/* Battery Options */}
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={isCharging} onChange={(e) => setIsCharging(e.target.checked)} />
                    <span className="text-sm" style={{ color: theme.textPrimary }}>Charging</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showBatteryPercent} onChange={(e) => setShowBatteryPercent(e.target.checked)} />
                    <span className="text-sm" style={{ color: theme.textPrimary }}>Show %</span>
                  </label>
                </div>

                {/* WiFi and Airplane */}
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showWifi} onChange={(e) => setShowWifi(e.target.checked)} />
                    <span className="text-sm" style={{ color: theme.textPrimary }}>WiFi</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={isAirplaneMode} onChange={(e) => setIsAirplaneMode(e.target.checked)} />
                    <span className="text-sm" style={{ color: theme.textPrimary }}>Airplane</span>
                  </label>
                </div>

                {/* WiFi Level */}
                {showWifi && (
                  <div>
                    <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                      WiFi Signal: {wifiLevel} bars
                    </label>
                    <input type="range" min="1" max="3" value={wifiLevel} onChange={(e) => setWifiLevel(Number(e.target.value))} className="w-full" />
                  </div>
                )}

                {/* Show Info Icon */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={showInfoIcon} onChange={(e) => setShowInfoIcon(e.target.checked)} />
                  <span className="text-sm" style={{ color: theme.textPrimary }}>Show info (i) icon</span>
                </label>

                {/* Container Size */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                    Width: {containerWidth}px
                  </label>
                  <input type="range" min="300" max="500" step="10" value={containerWidth} onChange={(e) => setContainerWidth(Number(e.target.value))} className="w-full" />
                </div>

                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                    Height: {containerHeight}px
                  </label>
                  <input type="range" min="600" max="900" step="10" value={containerHeight} onChange={(e) => setContainerHeight(Number(e.target.value))} className="w-full" />
                </div>
              </div>
            </details>

            {/* Background Section */}
            <details className="mb-5">
              <summary className="text-base font-semibold cursor-pointer mb-4" style={{ color: theme.textPrimary }}>
                Background
              </summary>

              <div className="flex flex-col gap-4">
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                    Background Type
                  </label>
                  <select
                    value={backgroundType}
                    onChange={(e) => setBackgroundType(e.target.value)}
                    className="w-full py-2 px-3 rounded-lg text-sm"
                    style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
                  >
                    <option value="gradient">Gradient</option>
                    <option value="color">Solid Color</option>
                    <option value="image">Custom Image</option>
                  </select>
                </div>

                {backgroundType === 'gradient' && (
                  <div>
                    <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                      Gradient Preset
                    </label>
                    <select
                      value={gradientPreset}
                      onChange={(e) => setGradientPreset(e.target.value)}
                      className="w-full py-2 px-3 rounded-lg text-sm"
                      style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
                    >
                      <option value="default-gray">Default Gray (iOS Style)</option>
                      <option value="dark-purple">Dark Purple</option>
                      <option value="sunset">Sunset</option>
                      <option value="ocean">Ocean</option>
                      <option value="forest">Forest</option>
                      <option value="night">Night</option>
                      <option value="pink">Pink</option>
                    </select>
                  </div>
                )}

                {backgroundType === 'color' && (
                  <div>
                    <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                      Background Color
                    </label>
                    <input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="w-full h-10 border-none rounded-lg cursor-pointer"
                    />
                  </div>
                )}

                {backgroundType === 'image' && (
                  <div>
                    <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                      Upload Wallpaper
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBackgroundUpload}
                      className="w-full p-2 rounded-lg text-sm"
                      style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
                    />
                  </div>
                )}
              </div>
            </details>

            {/* Call Section */}
            <details open className="mb-5">
              <summary className="text-base font-semibold cursor-pointer mb-4" style={{ color: theme.textPrimary }}>
                Call Settings
              </summary>

              <div className="flex flex-col gap-4">
                {/* Call Duration */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                    Call Duration (displayed)
                  </label>
                  <input
                    type="text"
                    value={callDuration}
                    onChange={(e) => setCallDuration(e.target.value)}
                    placeholder="00:00"
                    className="w-full py-2 px-3 rounded-lg text-sm"
                    style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
                  />
                </div>

                {/* Caller Name */}
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: theme.textPrimary }}>
                    Caller Name / Phone Number
                  </label>
                  <input
                    type="text"
                    value={callerName}
                    onChange={(e) => setCallerName(e.target.value)}
                    placeholder="Mirakle"
                    className="w-full py-2 px-3 rounded-lg text-sm"
                    style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
                  />
                </div>

                {/* Speaker Toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={isSpeakerOn} onChange={(e) => setIsSpeakerOn(e.target.checked)} />
                  <span className="text-sm" style={{ color: theme.textPrimary }}>Speaker Mode (highlight speaker icon)</span>
                </label>

                {/* Audio Section */}
                <div className="pt-4" style={{ borderTop: `1px solid ${theme.borderColor}` }}>
                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                    <input type="checkbox" checked={audioEnabled} onChange={(e) => setAudioEnabled(e.target.checked)} />
                    <span className="text-sm font-semibold" style={{ color: theme.textPrimary }}>Enable Audio Playback</span>
                  </label>

                  {audioEnabled && (
                    <>
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={handleAudioUpload}
                        className="w-full p-2 rounded-lg text-sm mb-3"
                        style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
                      />
                      <audio ref={audioRef} src={audioFile} onEnded={() => setIsPlaying(false)} />
                    </>
                  )}

                  {/* Audio Controls */}
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={playAudio}
                      disabled={!audioFile && audioEnabled}
                      icon="mdi:play"
                      className="bg-green-500 hover:bg-green-600 text-white"
                    >
                      Play
                    </Button>
                    <Button
                      onClick={pauseAudio}
                      icon="mdi:pause"
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      Pause
                    </Button>
                    <Button
                      onClick={stopAudio}
                      icon="mdi:stop"
                      variant="danger"
                    >
                      Stop
                    </Button>
                  </div>

                  {/* Timer toggle */}
                  <div className="mt-3 text-center">
                    <label className="flex items-center gap-2 cursor-pointer justify-center">
                      <input
                        type="checkbox"
                        checked={isCallActive}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            stopAudio();
                          } else {
                            setIsCallActive(true);
                          }
                        }}
                      />
                      <span className="text-sm" style={{ color: theme.textPrimary }}>Auto Timer Running</span>
                    </label>
                  </div>
                </div>
              </div>
            </details>

            {/* Labels Section */}
            <details className="mb-5">
              <summary className="text-base font-semibold cursor-pointer mb-4" style={{ color: theme.textPrimary }}>
                Edit Labels
              </summary>

              <div className="grid grid-cols-2 gap-3">
                {Object.entries(labels).map(([key, value]) => (
                  <div key={key}>
                    <label className="text-xs mb-1 block capitalize" style={{ color: theme.textSecondary }}>
                      {key}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => setLabels({ ...labels, [key]: e.target.value })}
                      className="w-full py-1.5 px-2.5 rounded-lg text-[13px]"
                      style={{ background: theme.bgPrimary, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary }}
                    />
                  </div>
                ))}
              </div>
            </details>

            {/* Download Button */}
            <LoadingButton
              onClick={exportImage}
              loading={isExporting}
              loadingText="Exporting..."
              icon="mdi:download"
              variant="primary"
              size="lg"
              className="w-full"
            >
              Download HD Image
            </LoadingButton>
          </div>

          {/* Preview Panel */}
          <div className="flex-[1_1_400px] flex flex-col items-center">
            {/* iPhone Frame */}
            <div
              className="rounded-[20px] p-6"
              style={{ background: '#f5f5f5', border: `1px solid ${theme.borderColor}` }}
            >
              <div
                ref={previewRef}
                className="rounded-[40px] overflow-hidden relative shadow-[0_20px_60px_rgba(0,0,0,0.3)]"
                style={{
                  width: `${containerWidth}px`,
                  height: `${containerHeight}px`,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                  ...getBackgroundStyle()
                }}
              >
                {/* Status Bar */}
                <div className="absolute top-0 left-0 right-0 py-3 px-6 flex justify-between items-center z-10">
                  {/* Left - Time & Icon */}
                  <div className="flex items-center gap-1">
                    <span className="text-[15px] font-semibold text-white">{deviceTime}</span>
                    {headerIcon && (
                      <span className="text-xs">
                        {headerIcon === 'location' && '📍'}
                        {headerIcon === 'moon' && '🌙'}
                        {headerIcon === 'silent' && '🔕'}
                      </span>
                    )}
                  </div>

                  {/* Right - Status Icons */}
                  <div className="flex items-center gap-1.5">
                    {!isAirplaneMode && renderSignalBars()}
                    {isAirplaneMode && <Icon icon="mdi:airplane" className="text-white text-base" />}
                    {!isAirplaneMode && (
                      <span className="text-xs font-semibold text-white">{networkType}</span>
                    )}
                    {showWifi && renderWifi()}
                    {showBatteryPercent && (
                      <span className="text-xs text-white">{batteryLevel}%</span>
                    )}
                    {renderBattery()}
                  </div>
                </div>

                {/* Info Icon */}
                {showInfoIcon && (
                  <div className="absolute top-[50px] right-5 z-10">
                    <Icon icon="mdi:information-outline" className="text-white/60 text-2xl" />
                  </div>
                )}

                {/* Call Info */}
                <div className="absolute top-[60px] left-0 right-0 text-center z-10">
                  <div className="text-xl text-white/90 mb-2 font-light tracking-wide">
                    {callDuration}
                  </div>
                  <div className="text-[38px] font-light text-white tracking-tight">
                    {callerName}
                  </div>
                </div>

                {/* Call Buttons Grid */}
                <div className="absolute bottom-[60px] left-1/2 -translate-x-1/2 grid grid-cols-3 gap-x-8 gap-y-5 w-[300px]">
                  <CallButtonWithSVG iconType="speaker" label={labels.speaker} isActive={isSpeakerOn} />
                  <CallButtonWithSVG iconType="facetime" label={labels.facetime} />
                  <CallButtonWithSVG iconType="mute" label={labels.mute} />
                  <CallButtonWithSVG iconType="add" label={labels.add} />
                  {/* End Button */}
                  <div className="flex flex-col items-center">
                    <div className="w-[70px] h-[70px] rounded-full bg-red-500 flex items-center justify-center">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                        <path d="M12 9C10.4 9 8.85 9.25 7.4 9.7V12.3C7.4 12.75 7.15 13.15 6.75 13.35C5.85 13.8 5 14.35 4.25 15C4.05 15.15 3.8 15.25 3.55 15.25C3.2 15.25 2.9 15.1 2.65 14.85L0.55 12.75C0.35 12.5 0.25 12.2 0.25 11.9C0.25 11.55 0.4 11.25 0.65 11.05C4 8.2 7.8 6.75 12 6.75C16.2 6.75 20 8.2 23.35 11.05C23.6 11.25 23.75 11.55 23.75 11.9C23.75 12.2 23.65 12.5 23.45 12.75L21.35 14.85C21.1 15.1 20.8 15.25 20.45 15.25C20.2 15.25 19.95 15.15 19.75 15C19 14.35 18.15 13.8 17.25 13.35C16.85 13.15 16.6 12.75 16.6 12.3V9.7C15.15 9.25 13.6 9 12 9Z" fill="white"/>
                      </svg>
                    </div>
                    <span className="text-white text-[13px] mt-2">{labels.end}</span>
                  </div>
                  <CallButtonWithSVG iconType="keypad" label={labels.keypad} />
                </div>

                {/* Bottom Bar (Home Indicator) */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[134px] h-[5px] bg-white/50 rounded-sm" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CallButtonWithSVG({ iconType, label, isActive = false }) {
  const iconColor = isActive ? '#333' : '#fff';

  const renderIcon = () => {
    switch (iconType) {
      case 'speaker':
        return (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <path d="M3 9v6h4l5 5V4L7 9H3z" fill={iconColor}/>
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" fill={iconColor}/>
            <path d="M14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill={iconColor}/>
          </svg>
        );
      case 'facetime':
        return (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" fill={iconColor}/>
          </svg>
        );
      case 'mute':
        return (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" fill={iconColor}/>
            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" fill={iconColor}/>
            <path d="M2.1 2.1L.69 3.51 5.17 8H5v3c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c1.48-.21 2.79-.85 3.83-1.75l3.66 3.66 1.41-1.41L2.1 2.1z" fill={iconColor} opacity="0.3"/>
            <line x1="3" y1="3" x2="21" y2="21" stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
      case 'add':
        return (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill={iconColor}/>
          </svg>
        );
      case 'keypad':
        return (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <circle cx="6" cy="6" r="2" fill={iconColor}/>
            <circle cx="12" cy="6" r="2" fill={iconColor}/>
            <circle cx="18" cy="6" r="2" fill={iconColor}/>
            <circle cx="6" cy="12" r="2" fill={iconColor}/>
            <circle cx="12" cy="12" r="2" fill={iconColor}/>
            <circle cx="18" cy="12" r="2" fill={iconColor}/>
            <circle cx="6" cy="18" r="2" fill={iconColor}/>
            <circle cx="12" cy="18" r="2" fill={iconColor}/>
            <circle cx="18" cy="18" r="2" fill={iconColor}/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div
        className="w-[70px] h-[70px] rounded-full flex items-center justify-center backdrop-blur-[20px]"
        style={{ background: isActive ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.15)' }}
      >
        {renderIcon()}
      </div>
      <span className="text-white text-[13px] mt-2 font-normal">{label}</span>
    </div>
  );
}

export default FakeCallGenerator;
